import { Fingerprint } from "lucide-react";
import type { Archetype } from "@/lib/archetypes";

interface Props {
  archetype: Archetype;
  confidence?: number; // 0-1
  evidenceTurns?: number[];
  variant?: "full" | "compact";
}

/**
 * Intelligence-dossier styling. Restrained, premium, archival.
 * Not therapy infographic. Not gamified.
 */
const ArchetypeCard = ({ archetype, confidence, evidenceTurns, variant = "full" }: Props) => {
  const compact = variant === "compact";
  const pct = confidence ? Math.round(confidence * 100) : null;

  return (
    <article
      className="relative rounded-xl border border-cyan/15 bg-card/40 backdrop-blur-sm overflow-hidden"
      style={{
        boxShadow: "inset 0 1px 0 hsl(184 100% 50% / 0.05)",
      }}
    >
      {/* Top dossier strip */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-border/30 bg-background/30">
        <div className="flex items-center gap-2">
          <Fingerprint className="w-3 h-3 text-cyan/60" strokeWidth={1.5} />
          <span className="text-[9px] font-mono tracking-[0.32em] uppercase text-muted-foreground/70">
            Behavioral Archetype
          </span>
        </div>
        {pct !== null && (
          <span className="text-[9px] font-mono tracking-[0.18em] uppercase text-cyan/50">
            Signal · {pct}%
          </span>
        )}
      </div>

      <div className="p-5 space-y-4">
        <div>
          <h3 className="text-lg font-semibold tracking-tight text-foreground">
            {archetype.name}
          </h3>
          <p className="text-sm text-foreground/70 leading-relaxed mt-1 font-light">
            {archetype.short}
          </p>
        </div>

        {!compact && (
          <>
            <Section label="Conversational Tendencies">
              <ul className="space-y-1.5">
                {archetype.tendencies.map((t, i) => (
                  <li key={i} className="text-xs text-foreground/75 leading-relaxed flex gap-2">
                    <span className="text-cyan/40 mt-1.5 w-1 h-1 rounded-full bg-cyan/40 shrink-0" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </Section>

            <Section label="Common Phrases">
              <div className="space-y-1">
                {archetype.phrases.map((p, i) => (
                  <p
                    key={i}
                    className="text-xs italic text-foreground/55 leading-relaxed font-light"
                  >
                    "{p}"
                  </p>
                ))}
              </div>
            </Section>

            <div className="grid grid-cols-1 gap-3 pt-1">
              <Section label="Escalation Style">
                <p className="text-xs text-foreground/70 leading-relaxed font-light">
                  {archetype.escalation}
                </p>
              </Section>
              <Section label="Emotional Effect">
                <p className="text-xs text-foreground/70 leading-relaxed font-light">
                  {archetype.emotionalEffect}
                </p>
              </Section>
            </div>
          </>
        )}

        {evidenceTurns && evidenceTurns.length > 0 && (
          <div className="pt-1 border-t border-border/30">
            <p className="text-[9px] font-mono tracking-[0.24em] uppercase text-muted-foreground/45">
              Observed at turn{evidenceTurns.length > 1 ? "s" : ""} {evidenceTurns.join(", ")}
            </p>
          </div>
        )}
      </div>
    </article>
  );
};

const Section = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <p className="text-[9px] font-mono tracking-[0.24em] uppercase text-cyan/55 mb-2">
      {label}
    </p>
    {children}
  </div>
);

export default ArchetypeCard;
